import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { useScrollBehavior } from '@/hooks/useScrollBehavior';
import { openUploadModal } from '@/features/ui/uiSlice';
import { setQuery, executeSearch, clearSearch, clearActiveAsset, fetchSearchTags, toggleModeTag, clearModeTags } from '@/features/search/searchSlice';
import { logout } from '@/features/auth/authSlice';
import { persistor } from '@/app/store';
import SearchBar from '@/components/ui/SearchBar/SearchBar';
import ThemeToggle from '@/components/ui/ThemeToggle/ThemeToggle';
import styles from './Navbar.module.css';

export default function Navbar() {
  const navScrolled = useAppSelector((s) => s.ui.navScrolled);
  const searchQuery = useAppSelector((s) => s.search.query);
  const uploadInProgress = useAppSelector((s) => s.ui.uploadInProgress);
  const authName = useAppSelector((s) => s.auth.name);
  const availableTags = useAppSelector((s) => s.search.availableTags);
  const activeModeTagIds = useAppSelector((s) => s.search.activeModeTagIds);
  const searchResults = useAppSelector((s) => s.search.results);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const avatarInitials = (() => {
    if (!authName) return '?';
    const parts = authName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  })();

  const [avatarOpen, setAvatarOpen] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useScrollBehavior();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    if (avatarOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [avatarOpen]);

  useEffect(() => {
    dispatch(fetchSearchTags());
  }, [dispatch]);

  const handleLogout = () => {
    // Clear auth credentials and auth localStorage keys
    dispatch(logout());
    // Clear in-memory search/active-asset state so Account B never sees Account A's data
    dispatch(clearSearch());
    dispatch(clearActiveAsset());
    // Purge redux-persist storage: removes persist:search (cached activeAsset)
    // and persist:auth so nothing is rehydrated into the next session
    persistor.purge();
    // Remove additional account-scoped localStorage keys not owned by authSlice
    localStorage.removeItem('last_hero_asset_id');
    setAvatarOpen(false);
    navigate('/signin', { replace: true });
  };

  const handleSearchChange = (value: string) => {
    if (window.scrollY > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    dispatch(setQuery(value));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length === 0) {
      dispatch(clearSearch());
      return;
    }
    if (value.length >= 2) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Mode is derived inside executeSearch from live Redux state, so no
      // stale-closure problem even with a 500 ms debounce.
      debounceRef.current = setTimeout(() => {
        dispatch(executeSearch({ query: value, limit: 15 }));
      }, 500);
    }
  };

  const handleModeTagToggle = (tagId: string) => {
    // toggleModeTag is synchronous — Redux state is updated before executeSearch
    // reads activeModeTagIds, so mode is always derived from the correct set.
    dispatch(toggleModeTag(tagId));
    if (searchQuery.length >= 2) {
      dispatch(executeSearch({ query: searchQuery, limit: 15 }));
    }
  };

  return (
    <nav className={`${styles.nav} ${navScrolled ? styles.navScrolled : ''}`}>
      {/* Search bar + mode chips */}
      <div
        className={styles.searchWrap}
        onFocus={() => {
          setInputFocused(true);
          if (window.scrollY > 0) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setInputFocused(false);
          }
        }}
      >
        <div className={styles.searchInputWrap}>
          <SearchBar
            placeholder="Search videos, projects, people..."
            value={searchQuery}
            onChange={handleSearchChange}
          />

          {/* Mode suggestion dropdown — shown while typing, hidden once results are showing */}
          {inputFocused && searchQuery.length >= 1 && availableTags.length > 0 && searchResults.length === 0 && (
            <div className={styles.modeDropdown} role="group" aria-label="Search mode suggestions">
              <span className={styles.modeDropdownLabel}>Search by</span>
              {availableTags.map((tag) => (
                <button
                  key={tag.id}
                  className={`${styles.modeChip} ${activeModeTagIds.includes(tag.id) ? styles.modeChipActive : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); handleModeTagToggle(tag.id); }}
                  aria-pressed={activeModeTagIds.includes(tag.id)}
                  title={tag.description}
                >
                  {tag.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Active mode chips — visible whenever filters are set */}
        {activeModeTagIds.length > 0 && (
          <div className={styles.activeModeRow}>
            {activeModeTagIds.map((id) => {
              const tag = availableTags.find((t) => t.id === id);
              if (!tag) return null;
              return (
                <button
                  key={id}
                  className={styles.activeModeChip}
                  onMouseDown={(e) => { e.preventDefault(); handleModeTagToggle(id); }}
                  aria-label={`Remove ${tag.label} filter`}
                  aria-pressed
                >
                  {tag.label}
                  <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                    <path d="M1 1l10 10M11 1L1 11" />
                  </svg>
                </button>
              );
            })}
            <button
              className={styles.clearModesBtn}
              onMouseDown={(e) => {
                e.preventDefault();
                dispatch(clearModeTags());
                if (searchQuery.length >= 2) dispatch(executeSearch({ query: searchQuery, limit: 15 }));
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Right side controls */}
      <div className={styles.navRight}>
        {/* + New video (upload) */}
        <button
          className={`${styles.newVideoBtn} ${uploadInProgress ? styles.newVideoBtnBusy : ''}`}
          onClick={() => !uploadInProgress && dispatch(openUploadModal())}
          disabled={uploadInProgress}
          aria-label={uploadInProgress ? 'Upload in progress' : 'New video'}
        >
          {!uploadInProgress && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          )}
          {uploadInProgress ? 'Uploading…' : 'New video'}
        </button>



        {/* Theme toggle */}
        <div className={styles.themeWrap}>
          <ThemeToggle />
        </div>

        {/* Avatar with popover */}
        <div
          className={`${styles.avatar} ${avatarOpen ? styles.avatarActive : ''}`}
          ref={avatarRef}
          role="button"
          tabIndex={0}
          aria-label={authName ? `User profile: ${authName}` : 'User profile'}
          aria-expanded={avatarOpen}
          data-tooltip={authName || undefined}
          onClick={() => setAvatarOpen((v) => !v)}
          onKeyDown={(e) => e.key === 'Enter' && setAvatarOpen((v) => !v)}
        >
          {avatarInitials}
          {avatarOpen && (
            <div className={styles.avatarPopover} role="menu">
              <button className={styles.avatarPopoverItem} role="menuitem" onClick={handleLogout}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
